import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as CryptoJS from 'crypto-js';
import { ManageColumnComponent } from '../../shared/manage-column/manage-column.component';
import { Observable } from 'rxjs';
import { IManageColumnInterface } from '../../model/commonModel';
import { MatTableDataSource } from '@angular/material/table';
import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ApiUrlHelper } from '../api-url-helper/api-url-helper';
import { ApiService } from '../api.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private KEY = CryptoJS.enc.Hex.parse('734edea7669b2e2091f5296f45c3d7006678c8c085e2ba13d548b884aba57998');
  private IV = CryptoJS.enc.Hex.parse('f1fb40dccf84d6ed69158274aec0fd3d');
  public emailRegex: RegExp =
    /^[a-zA-Z0-9.!#$%&'*\/=?^_`{|}~-]{1,64}@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
  public SignupemailRegex: RegExp =
    /^[a-zA-Z0-9.!#$%&'*\/=?^_`{|}~-]{1,64}@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

  public passwordRegex: RegExp =
    /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])[a-zA-Z0-9\W_]{8,}$/;
  public numberonlyRegex: RegExp = /^[0-9]*$/;
  public alphabetsRegex: RegExp = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
  public nameRegex: RegExp = /^[\p{L}\p{M}'’.`·•‐‑‒–—−.-]+(?: [\p{L}\p{M}'’.`·•‐‑‒–—−.-]+)*$/u;
  public validateNumberonlyRegex: RegExp = /^\d+$/;

  constructor(
    private dialog: MatDialog,
    private apiService: ApiService,
    private urls: ApiUrlHelper
  ) { }

  Encrypt(clearText: string): string {
    // As you're using Encoding.Unicde in .net, we have to use CryptoJS.enc.Utf16LE here.
    return CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf16LE.parse(clearText),
      this.KEY,
      {
        iv: this.IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    ).toString();
  }

  Decrypt(cipherText: string): string {
    // As you're using Encoding.Unicde in .net, we have to use CryptoJS.enc.Utf16LE here.
    let decrypt = CryptoJS.AES.decrypt(cipherText, this.KEY, {
      iv: this.IV,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    let utf16String = CryptoJS.enc.Utf16LE.stringify(decrypt);
    return utf16String
      .replaceAll('\x00', '')
      .replaceAll(/[\u0000-\u001F\u007F-\u009F]/g, '');
    // return utf16String.replaceAll('\x00', '').replaceAll(/&apos;/g, "'").replaceAll(/&quot;/g, '"').replaceAll(/[\u0000-\u001F\u007F-\u009F]/g, '');
  }

  public replaceQuote(string: string): string {
    return string.replaceAll(/&apos;/g, "'").replaceAll(/&quot;/g, '"');
  }

  //this function will be in use only if you change the encryptionKey,
  //so next time call this functin from somewhere and generate KEY and IV and replace with current one.
  generateKEYNIV() {
    const encryptionKey = CryptoJS.enc.Utf8.parse('EA34FF3E-JU84-1992-WN01-BB81D9564169');
    const salt = CryptoJS.enc.Base64.parse('VmFpYkhhdlBhcmVraA=='); // this is the byte array in .net
    const iterations = 100000; // https://learn.microsoft.com/en-us/dotnet/api/system.security.cryptography.rfc2898derivebytes?view=netcore-3.1
    const CryptoAlgo = CryptoJS.algo.SHA512;
    const keyAndIv = CryptoJS.PBKDF2(encryptionKey, salt, {
      keySize: 256 / 32 + 128 / 32,
      iterations: iterations,
      hasher: CryptoAlgo,
    }); // so PBKDF2 in CryptoJS is direct in that it
    // always begins at the beginning of the password, whereas the .net
    // implementation offsets by the last length each time .GetBytes() is called
    // so we had to generate a Iv + Salt password and then split it
    const hexKeyAndIv = CryptoJS.enc.Hex.stringify(keyAndIv);

    const stringKEY = hexKeyAndIv.substring(0, 64);
    // this.KEY = stringKEY;
    const stringIV = hexKeyAndIv.substring(64, hexKeyAndIv.length);
    // this.IV = stringIV;
  }

  checkColumnPreference(
    displayedColumns: IManageColumnInterface[],
    listName: string
  ) {
    let apiUrl = this.urls.apiUrl.common.getColumnPreferences + '/' + listName;
    this.apiService.doGet(apiUrl).subscribe({
      next: (data) => {
        if (data?.Success) {
          const savedColumns = JSON.parse(data.Data);

          displayedColumns.length = 0;
          displayedColumns.push(...savedColumns);
        }
      },
      error: () => { },
      complete: () => { },
    });
    // Still return the original reference
    return displayedColumns;
  }

  openManageColumnsDialog(
    event: MouseEvent,
    displayedColumns: IManageColumnInterface[],
    listName?: string
  ): Observable<IManageColumnInterface[]> {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const dialogWidth = 300;
    const availableHeight = window.innerHeight - rect.bottom;

    const dialogRef = this.dialog.open(ManageColumnComponent, {
      width: `${dialogWidth}px`,
      position: {
        top: `${rect.bottom}px`,
        left: `${rect.right - dialogWidth}px`,
      },
      maxHeight: `${availableHeight}px`, // Dynamically setting max height
      data: { columns: displayedColumns, listName: listName ?? '' },
      panelClass: 'custom-dialog-container',
    });

    return dialogRef.afterClosed();
  }

  generateColumns(
    data: any[],
    manageColumnsdisplay: {
      column: string;
      columnTitle: string;
      type: 'text' | 'date' | 'number' | 'isscore';
      data: [];
      isShow?: boolean;
    }[] = []
  ) {
    const columns: any[] = manageColumnsdisplay.filter(
      (col) => col.isShow && col.columnTitle
    );
    return columns;
  }

  applyColumnFilters<T>(
    dataSource: MatTableDataSource<T>,
    data: T[],
    filters: { [key: string]: any },
    manageColumnsdisplay: IManageColumnInterface[]
  ) {
    let filteredData = [...data];

    // Object.keys(filters).forEach((column) => {
    //   const filterValue = filters[column];
    //   const columnConfig = manageColumnsdisplay.find(
    //     (col) => col.column === column
    //   );

    //   if (typeof filterValue === 'string' && columnConfig?.type === 'text') {
    //     filteredData = filteredData.filter((row) => {
    //       const rowValue = row[column as keyof typeof row];

    //       if (typeof rowValue === 'string') {
    //         return rowValue.toLowerCase().includes(filterValue.toLowerCase());
    //       }

    //       return false;
    //     });
    //   }

    //   if (
    //     columnConfig?.type === 'date' &&
    //     filterValue &&
    //     typeof filterValue !== 'string'
    //   ) {
    //     const { startDate, endDate } = filterValue as {
    //       startDate: Date | null;
    //       endDate: Date | null;
    //     };
    //     filteredData = filteredData.filter((row: any) => {
    //       const rowDate = new Date(row[column as keyof typeof row]);
    //       rowDate.setHours(0, 0, 0, 0);
    //       if (startDate && endDate) {
    //         return rowDate >= startDate && rowDate <= endDate;
    //       }

    //       return true;
    //     });
    //   }
    // });

    dataSource.data = filteredData;
  }

  formatDateToYMD(date: Date | string): string {
    // const d = date instanceof Date ? date : new Date(date);
    // return (
    //   d.getFullYear() +
    //   '-' +
    //   String(d.getMonth() + 1).padStart(2, '0') +
    //   '-' +
    //   String(d.getDate()).padStart(2, '0') 
    //   // + 'T23:59:59.000Z'
    // );
    const d = date instanceof Date ? date : new Date(date);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  }

  // openFilterDialog(
  //   data: any[],
  //   manageColumnsdisplay: IManageColumnInterface[],
  //   appliedFilters: any,
  //   applyFilterCallback: (filters: any) => void
  // ) {
  //   const columns = this.generateColumns(
  //     data,
  //     manageColumnsdisplay.map((col) => ({
  //       column: col.column,
  //       columnTitle: col.columnTitle,
  //       type: col.type ?? 'text',
  //       data: [],
  //       isShow: col.isShow,
  //     }))
  //   );

  //   const dialogRef = this.dialog.open(ColumnFilterComponent, {
  //     width: '900px',
  //     data: { columns, data, filters: appliedFilters },
  //   });

  //   dialogRef.afterClosed().subscribe((filters) => {
  //     console.log('Applied filters:', filters);
  //     if (filters) {
  //       applyFilterCallback(filters);
  //     }
  //   });
  // }

  joinWithComma(...args: any[]): string {
    return args.filter((value) => value).join(', ');
    // return args
    //   .filter(value => value !== null && value !== undefined && value !== '') // Remove null, undefined, empty values
    //   .join(', ');
  }

  formatDate(event: Event, IsFutureDate: boolean): any {
    if (IsFutureDate == true) {
      // format date with restrict future date
      const input = event.target as HTMLInputElement;
      let value = input.value.replace(/\D/g, '');
      let isFutureDate = false;

      if (value.length > 2 && value.length <= 4) {
        value = `${value.slice(0, 2)}/${value.slice(2)}`;
      }
      else if (value.length > 4) {
        value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;

        const [month, day, year] = value.split('/');
        const enteredDate = new Date(`${year}-${month}-${day}`);
        const today = new Date();

        enteredDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (enteredDate > today) {
          input.value = '';
          isFutureDate = true;
        }
      }
      input.value = value;
      return isFutureDate;
    }

    else {
      // format date
      const input = event.target as HTMLInputElement;
      let value = input.value.replace(/\D/g, '');

      if (value.length > 2 && value.length <= 4) {
        value = `${value.slice(0, 2)}/${value.slice(2)}`;
      } else if (value.length > 4) {
        value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
      }
      input.value = value;

      //19/05 update
      // const input = event.target as HTMLInputElement;
      // let value = input.value.replace(/\D/g, '').slice(0, 8); // Max 8 digits

      // let formattedValue = '';

      // if (value.length >= 5) {
      //   formattedValue = `${value.slice(0, 2).padStart(2, '0')}/${value.slice(2, 4).padStart(2, '0')}/${value.slice(4, 8)}`;
      // } else if (value.length >= 3) {
      //   formattedValue = `${value.slice(0, 2).padStart(2, '0')}/${value.slice(2).padStart(2, '0')}`;
      // } else {
      //   formattedValue = value;
      // }

      // input.value = formattedValue;

      // // Optional: prevent form control from updating with invalid dates
      // const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
      // if (datePattern.test(formattedValue)) {
      //   return formattedValue;
      // }
    }
  }

  isAtLeastAge(enteredDate: any, age: number): boolean {
    if (!enteredDate || age == null) return false;

    let inputDate: Date;

    // If enteredDate is a string, try parsing it
    if (typeof enteredDate === 'string') {
      const [mm, dd, yyyy] = enteredDate.split('/');
      if (!mm || !dd || !yyyy || mm.length !== 2 || dd.length !== 2 || yyyy.length !== 4) return false;
      inputDate = new Date(`${yyyy}-${mm}-${dd}`);
    }
    // If enteredDate is a Date object
    else if (enteredDate instanceof Date && !isNaN(enteredDate.getTime())) {
      inputDate = new Date(enteredDate); // Create a copy
    } else {
      return false; // Invalid format
    }

    // Remove time part
    inputDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minAllowedDate = new Date(today);
    minAllowedDate.setFullYear(today.getFullYear() - age);

    return inputDate <= minAllowedDate;
  }



  onDateBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const parts = input.value.split('/');

    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];

      if (year.length === 4) {
        input.value = `${day}/${month}/${year}`;
      }
    }
  }
}

export class ListNames {
  public static MyQuotesListName = 'quotes-list';
  public static MyLeadsListName = 'leads-list';
  public static SoldLeadsListName = 'sold-leads-list';
  public static MyMatchesListName = 'my-matches-list';
  public static FirmBalanceListName = 'firm-balance-list';
  public static CustomerReferralsListName = 'customer-referrals';
  public static AdminReferralsListName = 'admin-referrals';
  public static FirmReferralsListName = 'firm-referrals';
  // FirmRef feature - firm's own referrals grid
  public static FirmReferralsByFirmListName = 'firm-referrals-firm';
}

export function passwordMatchValidator(
  password: string,
  confirmPassword: string
): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const passwordControl = formGroup.get(password);
    const confirmPasswordControl = formGroup.get(confirmPassword);

    if (!passwordControl || !confirmPasswordControl) {
      return null;
    }

    if (
      confirmPasswordControl.errors &&
      !confirmPasswordControl.errors['passwordMismatch']
    ) {
      return null; // Return if another validator has already found an error
    }

    if (passwordControl.value !== confirmPasswordControl.value) {
      confirmPasswordControl.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      confirmPasswordControl.setErrors(null);
    }

    return null;
  };


}

export function eachPersonLessThanEachOccurrenceValidator(
  form: AbstractControl,
  personControlName: string,
  occurrenceControlName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const personValue = form.get(personControlName)?.value;
    const occurrenceValue = form.get(occurrenceControlName)?.value;

    if (personValue !== null && occurrenceValue !== null && personValue > occurrenceValue) {
      return { eachPersonRangeInvalid: true };
    }
    return null;
  };
}

export function formatUSPhoneNumber(input: string): string {
  let value = input || '';

  // Remove non-digit characters
  value = value.replace(/\D/g, '');

  // Trim to 10 digits
  value = value.substring(0, 10);

  // Apply formatting
  if (value.length > 6) {
    return value.replace(/(\d{3})(\d{3})(\d{1,4})/, '$1-$2-$3');
  } else if (value.length > 3) {
    return value.replace(/(\d{3})(\d{1,3})/, '$1-$2');
  } else {
    return value;
  }
}

export function initApp(storageService: StorageService): () => Promise<void> {
  return () => new Promise<void>((resolve) => {
    const hasVisited = sessionStorage.getItem('hasVisited');
    if (!hasVisited) {
      // storageService.clearAll();
      sessionStorage.setItem('hasVisited', 'true');
    }
    resolve();
  });
}

//For coverage value range master (Not Desired)
export function restrictedWordsValidator(restrictedWords: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || typeof control.value !== 'string') return null;

    const input = control.value.trim();

    // Rule 1: Disallow multiple spaces or leading/trailing spaces
    const validSpacing = /^\S+(?: \S+)*$/.test(input);
    if (!validSpacing) {
      return { invalidSpacing: true };
    }

    // Normalize: remove all spaces and convert to lowercase
    const normalizedInput = input.replace(/\s+/g, '').toLowerCase();

    // Rule 2: Check against restricted words
    const isRestricted = restrictedWords.some(
      word => word.replace(/\s+/g, '').toLowerCase() === normalizedInput
    );

    if (isRestricted) {
      return { restrictedWord: true };
    }

    return null;
  };
}

export function parseDateOnly(value: string | null): Date | null {
  if (!value) {
    return null;
  }

  const [datePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);

  return new Date(year, month - 1, day);
}

export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`; // Returns "YYYY-MM-DD" without timezone shift
}